import React, { useEffect, useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import map from 'lodash/map'
import filter from 'lodash/filter'
import classnames from 'classnames'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import sortSearchResults from '@Utils/SortSearchResults'
import TranslationLanguage from './TranslationLanguage'
import Arrow from '@Images/languages_dropdown_arrow.svg'
import Alert from '@Images/project_setup_form_alert.svg'

import {
  ITranslationSettings,
} from '@SiteModules/Project/types'

import {
  IGuideline,
} from '@SiteModules/Guideline/types'

import '@SiteComponents/SearchDropdown/SearchDropdown.scss'
import './TranslationLanguages.scss'

const DROPDOWN_OFFSET_Y = 50 // px

export interface IDropdownPosition {
  top: string
  left: string
}

interface TranslationLanguagesProps {
  readonly projectVersionLanguageIsTranslatable: boolean
  readonly isNewProject: boolean
  readonly isNewSubtitlesProject: boolean
  readonly newProjectVersionLanguage: string
  readonly existingProjectVersionLanguage: string
  readonly newTranslationSettings: ITranslationSettings[]
  readonly existingTranslationLanguages: string[]
  readonly availableTranslationLanguages: string[]
  readonly showAlert: boolean
  readonly defaultTranslationGuidelineOption: string | null
  readonly translationGuidelines: IGuideline[]
  readonly cannotImportScript: boolean
  readonly translationProvider: string
  readonly fetchingTranslationLanguages: boolean
  readonly shouldTranslate: boolean
  readonly shouldRepurpose: boolean
  setNewTranslationSettings(newTranslationSettings: ITranslationSettings[]): void
  handleFetchTranslationLanguages(): void
  setShouldTranslate(newShouldTranslate: boolean): void
}

const TranslationLanguages: React.FC<TranslationLanguagesProps> = props => {
  const {
    projectVersionLanguageIsTranslatable,
    isNewProject,
    isNewSubtitlesProject,
    newProjectVersionLanguage,
    existingProjectVersionLanguage,
    newTranslationSettings,
    existingTranslationLanguages,
    availableTranslationLanguages,
    showAlert,
    defaultTranslationGuidelineOption,
    translationGuidelines,
    cannotImportScript,
    translationProvider,
    fetchingTranslationLanguages,
    shouldTranslate,
    shouldRepurpose,
  } = props

  const setToManual = (languages: string[]) => languages.map((language) => {
    if (language.endsWith('(manual)')) { return language }

    return language + ' (manual)'
  })

  const [showAddTranslationLanguages, setShowAddTranslationLanguages] = useState(false)
  const [searchTextForAdding, setSearchTextForAdding] = useState('')
  const [searchTextForReplacing, setSearchTextForReplacing] = useState('')
  const [relevantTranslationLanguages, setRelevantTranslationLanguages] = useState(projectVersionLanguageIsTranslatable ? availableTranslationLanguages : setToManual(availableTranslationLanguages))
  const [inputForAddingIsFocused, setInputForAddingIsFocused] = useState(false)
  const [inputForReplacingIsFocused, setInputForReplacingIsFocused] = useState(false)
  const [indexForReplacingTranslationLanguages, setIndexForReplacingTranslationLanguages] = useState(-1)

  const projectVersionLanguage = isNewProject ? newProjectVersionLanguage : existingProjectVersionLanguage
  const hasRelevantTranslationLanguages = relevantTranslationLanguages.length > 1 || (relevantTranslationLanguages.length === 1 && relevantTranslationLanguages[0] !== projectVersionLanguage) // Do not only show the source language

  const prevProjectVersionLanguageIsTranslatable = useRef<boolean>()
  const prevNewProjectVersionLanguage = useRef<string>()

  const languageWireIsTranslationProvider = translationProvider === 'language_wire'

  const showTranslationSwitch = isNewSubtitlesProject || shouldRepurpose
  const showTranslationSectionHeader = !showTranslationSwitch
  const shouldShowTranslationLanguages = (showTranslationSwitch && shouldTranslate) || !showTranslationSwitch
  const switchCaption = shouldRepurpose ? 'Translate the content' : 'Translate the subtitles'
  const shouldShowCommentAboutLaterTranslation = shouldRepurpose && !shouldTranslate

  const handleCloseNewTranslationLanguages = () => {
    setIndexForReplacingTranslationLanguages(-1)
  }

  useClickOutsideListener('TranslationLanguages__add-element', setShowAddTranslationLanguages, showAddTranslationLanguages)
  useClickOutsideListener('TranslationLanguages__new-translations-item-element', handleCloseNewTranslationLanguages, indexForReplacingTranslationLanguages !== -1)

  useEffect(() => {
    const projectVersionLanguageIsTranslatableChanged =
      typeof(prevProjectVersionLanguageIsTranslatable.current) === 'boolean' && (prevProjectVersionLanguageIsTranslatable.current !== projectVersionLanguageIsTranslatable)
    const newProjectVersionLanguageChanged =
      prevNewProjectVersionLanguage.current !== newProjectVersionLanguage

    if (projectVersionLanguageIsTranslatableChanged || (newProjectVersionLanguageChanged && languageWireIsTranslationProvider)) {
      props.setNewTranslationSettings([]) // To have the correct suffix (manual)

      if (languageWireIsTranslationProvider) {
        props.handleFetchTranslationLanguages()
      }
    } else if (newProjectVersionLanguageChanged) {
      props.setNewTranslationSettings(
        filter(
          newTranslationSettings,
          currentNewTranslationSettings => currentNewTranslationSettings.language !== newProjectVersionLanguage
        )
      )
    }

    prevProjectVersionLanguageIsTranslatable.current = projectVersionLanguageIsTranslatable
    prevNewProjectVersionLanguage.current = newProjectVersionLanguage
  }, [projectVersionLanguageIsTranslatable, newProjectVersionLanguage, newTranslationSettings])

  useEffect(() => {
    const searchText = searchTextForAdding.length > 0 ? searchTextForAdding : searchTextForReplacing

    const baseLanguages = projectVersionLanguageIsTranslatable
  ? availableTranslationLanguages
  : setToManual(availableTranslationLanguages)

  const newRelevantTranslationLanguages = filter(
    baseLanguages,
    language => language.toLowerCase().includes(searchText.toLowerCase())
  )

  newRelevantTranslationLanguages.sort(sortSearchResults(searchText))

  setRelevantTranslationLanguages(newRelevantTranslationLanguages)
  }, [availableTranslationLanguages, searchTextForAdding, searchTextForReplacing, projectVersionLanguageIsTranslatable])

  useEffect(() => {
    if (!showAddTranslationLanguages) {
      setSearchTextForAdding('')
    }
  }, [showAddTranslationLanguages])

  useEffect(() => {
    const scrollContainer = document.querySelector('.TranslationLanguages__new-translations-items') as HTMLDivElement
    const hasClosedTranslationLanguages = indexForReplacingTranslationLanguages === -1
    if (hasClosedTranslationLanguages) {
      setSearchTextForReplacing('')
    }

    if (scrollContainer) {
      scrollContainer.style.overflowY = hasClosedTranslationLanguages ? 'scroll' : 'hidden'
    }
  }, [indexForReplacingTranslationLanguages])

  const toggleDropdown = () => {
    setShowAddTranslationLanguages(!showAddTranslationLanguages)
  }

  const handleShowTranslationLanguages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newShouldTranslate = e.target.checked
    props.setShouldTranslate(newShouldTranslate)

    if (!newShouldTranslate) {
      props.setNewTranslationSettings([])
    }
  }

  const renderSearchInput = (currentTranslationLanguage?: string) => {
    const isInputForReplacing = !!currentTranslationLanguage

    const handleOnClick = () => {
      if (isInputForReplacing || showAddTranslationLanguages) { return }

      setShowAddTranslationLanguages(true)
    }

    const handleSetFocus = () => {
      isInputForReplacing ? setInputForReplacingIsFocused(true) : setInputForAddingIsFocused(true)
    }

    const handleRemoveFocus = () => {
      isInputForReplacing ? setInputForReplacingIsFocused(false) : setInputForAddingIsFocused(false)
    }

    const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchText = (e.target as HTMLInputElement).value
      isInputForReplacing ? setSearchTextForReplacing(newSearchText) : setSearchTextForAdding(newSearchText)
    }

    return (
      <div
        className={classnames(
          'SearchDropdown__input-wrapper',
          { 'SearchDropdown__input-wrapper-focused': isInputForReplacing ? inputForReplacingIsFocused : inputForAddingIsFocused },
          { 'SearchDropdown__input-wrapper-alert': showAlert && !isInputForReplacing && !inputForAddingIsFocused },
        )}
        onClick={handleOnClick}
      >
        <input
          placeholder={isNewProject ? 'Add a new language...' : 'Select...'}
          autoComplete="off"
          value={isInputForReplacing ? searchTextForReplacing : searchTextForAdding}
          onChange={handleSearchTextChange}
          onFocus={handleSetFocus}
          onBlur={handleRemoveFocus}
          disabled={fetchingTranslationLanguages}
        />
        <Arrow
          onClick={toggleDropdown}
        />
      </div>
    )
  }

  const renderTranslationLanguagesList = (languageIndexToReplace?: number) => {
    const isLanguagesListForReplacing = typeof(languageIndexToReplace) === 'number'

    return (
      map(relevantTranslationLanguages, (relevantTranslationLanguage, index) => {
        const isProjectVersionLanguage = projectVersionLanguageIsTranslatable ? projectVersionLanguage === relevantTranslationLanguage : setToManual([projectVersionLanguage])[0] === relevantTranslationLanguage
        const newTranslationLanguages = map(newTranslationSettings, newTranslationSetting => newTranslationSetting.language)
        const alreadyAddedTranslationLanguage = existingTranslationLanguages.concat(newTranslationLanguages).includes(relevantTranslationLanguage)
        const isSelectable = !isProjectVersionLanguage && !alreadyAddedTranslationLanguage

        const handleAddToNewTranslationLanguages = () => {
          if (!isSelectable) { return }

          if (isLanguagesListForReplacing) {
            const translationSettingsToReplace = newTranslationSettings[languageIndexToReplace]
            props.setNewTranslationSettings([...newTranslationSettings.slice(0, languageIndexToReplace), { language: relevantTranslationLanguage, translationGuidelineOption: translationSettingsToReplace.translationGuidelineOption }, ...newTranslationSettings.slice(languageIndexToReplace + 1)])
            setIndexForReplacingTranslationLanguages(-1)
          } else {
            props.setNewTranslationSettings(newTranslationSettings.concat([{ language: relevantTranslationLanguage, translationGuidelineOption: defaultTranslationGuidelineOption }]))
            setShowAddTranslationLanguages(false)
          }
        }

        return (
          <li
            key={index}
            className={classnames(
              'SearchDropdown__item',
              { 'SearchDropdown__item-selectable': isSelectable }
            )}
            onClick={handleAddToNewTranslationLanguages}
          >
            <div className="SearchDropdown__item-name">
              {relevantTranslationLanguage}
            </div>
            <div className="SearchDropdown__item-info">
              {isProjectVersionLanguage && 'Source language'}
              {alreadyAddedTranslationLanguage && 'Already added'}
            </div>
          </li>
        )
      })
    )
  }

  const renderNoOptionFound = () => {
    return (
      <li className="SearchDropdown__item SearchDropdown__item-not-found">
        <div className="SearchDropdown__item-name">
          No language found
        </div>
      </li>
    )
  }

  const renderTranslationLanguages = (style: IDropdownPosition, languageIndexToReplace?: number) => {
    const isDropdownForReplacing = typeof(languageIndexToReplace) === 'number'

    return ReactDOM.createPortal(
      (
        <ul
          className={classnames(
            'SearchDropdown__items',
            { 'TranslationLanguages__add-element': !isDropdownForReplacing },
            { 'TranslationLanguages__new-translations-item-element': isDropdownForReplacing },
          )}
          style={style}
        >
          {hasRelevantTranslationLanguages ? renderTranslationLanguagesList(languageIndexToReplace) : renderNoOptionFound()}
        </ul>
      ),
      document.getElementById('site-container')!
    )
  }

  const newTranslationsList = map(newTranslationSettings, (currentNewTranslationSettings, index) => {
    return (
      <TranslationLanguage
        key={index}
        index={index}
        currentNewTranslationSettings={currentNewTranslationSettings}
        newTranslationSettings={newTranslationSettings}
        setNewTranslationSettings={props.setNewTranslationSettings}
        indexForReplacingTranslationLanguages={indexForReplacingTranslationLanguages}
        setIndexForReplacingTranslationLanguages={setIndexForReplacingTranslationLanguages}
        dropdownOffsetY={DROPDOWN_OFFSET_Y}
        renderSearchInput={renderSearchInput}
        renderTranslationLanguages={renderTranslationLanguages}
        translationGuidelines={translationGuidelines}
        cannotImportScript={cannotImportScript}
      />
    )
  })

  const renderNewTranslationLanguages = () => {
    return (
      <ul className="TranslationLanguages__new-translations-items">
        {newTranslationsList}
      </ul>
    )
  }

  const renderAlert = () => {
    return (
      <div className="SetupProjectPopup__alert">
        <Alert />
        You need to select the language you want to translate your video into.
      </div>
    )
  }

  const renderTranslationSwitch = () => {
    return (
      <div className="TranslationLanguages__switch">
        <label className="TranslationLanguages__switch-label">
          <input
            type="checkbox"
            checked={shouldTranslate}
            onChange={handleShowTranslationLanguages}
          />
          <span className="TranslationLanguages__switch-slider" />
        </label>
        {switchCaption}
      </div>
    )
  }

  const dropdownParentElement = document.getElementById('translation_languages_add')
  let dropdownX = 0
  let dropdownY = 0

  if (dropdownParentElement) {
    const dropdownParentElementBounds = dropdownParentElement.getBoundingClientRect()
    dropdownX = dropdownParentElementBounds.left
    dropdownY = dropdownParentElementBounds.top + DROPDOWN_OFFSET_Y
  }

  const dropdownPositioningStyle = {
    top: `${dropdownY}px`,
    left: `${dropdownX}px`,
  }

  const renderTranslationLanguagesMain = () => {
    return (
      <div className="TranslationLanguages__languages">
        <div className="TranslationLanguages__main">
          {showTranslationSectionHeader && (
            <div className="SetupProjectPopup__section-header">
              {isNewProject ? 'Translation languages' : 'Add translation languages'}
            </div>
          )}
          {renderNewTranslationLanguages()}
          <div
            className="TranslationLanguages__add TranslationLanguages__add-element"
            id="translation_languages_add"
          >
            {renderSearchInput()}
            {showAddTranslationLanguages && renderTranslationLanguages(dropdownPositioningStyle)}
          </div>
          {showAlert && renderAlert()}
        </div>
      </div>
    )
  }

  const renderCommentAboutLaterTranslation = () => {
    return (
      <div className="TranslationLanguages__comment">
        You can enable translation later at any time.
      </div>
    )
  }

  return (
    <div className="TranslationLanguages">
      {showTranslationSwitch && renderTranslationSwitch()}
      {shouldShowTranslationLanguages && renderTranslationLanguagesMain()}
      {shouldShowCommentAboutLaterTranslation && renderCommentAboutLaterTranslation()}
    </div>
  )
}

export default TranslationLanguages
